'use client';

import { getTranslation } from '@/lib/utils';
import { SideNavigatorTab } from '@/components/DiscoverPageSections/SideNavigator/SideNavigatorTab';
import { NewPageSection } from '@/types/TermPage';
import { HTMLAttributes, useEffect, useState } from 'react';
import { Locale } from '../../../../i18n.config';
import { useFAQ } from '@/hooks/useFAQContext';

interface IFAQSideNavigatorTabProps extends HTMLAttributes<HTMLDivElement> {
  sections: NewPageSection[];
  lang: Locale;
}

export const FAQSideNavigatorTab: React.FC<IFAQSideNavigatorTabProps> = ({
  sections,
  lang,
}) => {
  const { hash, changeHash, selectedSubTab } = useFAQ();
  const [expandedTab, setExpandedTab] = useState(0);

  const expansiveTabs = sections
    .filter((section) => section.hasSubsections)
    .map((section, tabIndex) => {
      return {
        title: getTranslation(section.pageSectionTranslation, lang).title,
        subtabs: section.subsections.map((subSection, subTabIndex) => ({
          title:
            getTranslation(subSection.pageSectionTranslation, lang)?.title ||
            '',
          href: `#faq-${tabIndex + 1}-${subTabIndex + 1}`,
          page: `#faq-${tabIndex + 1}-${subTabIndex + 1}`,
          description:
            getTranslation(subSection.pageSectionTranslation, lang)
              ?.description || '',
        })),
      };
    });

  useEffect(() => {
    const handleHashChange = () => {
      const hashCode = window.location.hash;
      changeHash(hashCode);
      const tabToExpand = Number(hashCode.match(/\d+/));
      setExpandedTab(tabToExpand);
    };

    window.addEventListener('hashchange', handleHashChange);
    return () => {
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, [changeHash]);

  return (
    <aside className="w-full desktop:w-1/4 block h-full pr-0 top-[108px] left-0 overflow-y-auto shadow-[0_18px_20px_0_rgba(0,0,0,.1)]">
      <ul className="h-full flex flex-col items-start space-y-5 overflow-x-hidden">
        {expansiveTabs.map((tab, index) => (
          <SideNavigatorTab
            className="!mt-0 !px-4 !py-5 ! w-full !border-b-2"
            onSubTabClick={(e, page) => {
              e.preventDefault();
              changeHash(page);
            }}
            onTabClick={() => {
              setExpandedTab((prevState) =>
                prevState !== index + 1 ? index + 1 : -1,
              );
            }}
            key={index}
            tabId={index + 1}
            expandedTab={expandedTab}
            expandedSubTab={selectedSubTab}
            tab={tab}
            currentPage={hash}
          />
        ))}
      </ul>
    </aside>
  );
};
